import { useHandler } from "@redotech/react-util/hook";
import {
  MACRO_TEXT_BACKGROUND,
  MACRO_TEXT_COLOR,
  type MacroVariable,
} from "@redotech/redo-support-common/macro";
import { KEYS, type TElement } from "platejs";
import { useCallback, useMemo, useState } from "react";
import type { UseMessageEditorResult } from "../components/message-editor/use-message-editor";
import { filterMacroVariables } from "../components/variable-list";

/**
 * State + helpers for `/`-triggered variable autocomplete inside the macro
 * message editor. Mirrors `use-mention-autocomplete` but is simpler:
 * variables are a static, finite set, and the inserted token is plain literal
 * text (no entity tracking, no draft persistence).
 *
 * The trigger regex matches a `/` after a word boundary (or beginning of text)
 * followed by zero-or-more name characters. Trailing whitespace ends the
 * trigger so the menu closes after the user types a space.
 */

export interface VariableAutocompleteState {
  readonly isOpen: boolean;
  readonly query: string;
  readonly filteredVariables: readonly MacroVariable[];
  readonly focusedIndex: number;
  readonly anchorRect: DOMRect | null;
  readonly setFocusedIndex: (index: number) => void;
  readonly close: () => void;
  readonly handleSelectVariable: (variable: MacroVariable) => void;
  readonly handleEditorChange: () => void;
}

/** Trailing `/<letters/digits/spaces>` token. Capture group 1 is the query. */
const TRIGGER_REGEX = /(?:^|\s)\/([\p{L}\p{N} _-]{0,40})$/u;

/**
 * Insert a link-variable token at the editor's current caret.
 *
 * Variables with `isLink: true` (e.g. `recent_order_tracking_url_link`) resolve
 * to a URL at send time.  We insert them as a Plate.js link element so the
 * outbound email HTML contains a real `<a href="…">` after the backend
 * substitutes the placeholder URL.  The `url` is stored as the raw placeholder
 * `{variable_name}` — `StaticLinkElement` in `editor-plugins.ts` serializes it
 * as an `<a>` and the backend replaces the placeholder before sending.
 */
function insertLinkVariableToken(
  editor: NonNullable<UseMessageEditorResult["editor"]>,
  variable: MacroVariable,
): void {
  const tokenText = `{${variable.variable}}`;
  editor.tf.insertNodes({
    type: KEYS.link,
    url: tokenText,
    target: "_blank",
    children: [
      {
        text: tokenText,
        [KEYS.color]: MACRO_TEXT_COLOR,
        [KEYS.backgroundColor]: MACRO_TEXT_BACKGROUND,
      },
    ],
  } as TElement);
  // Move selection to after the inserted link node, then insert a trailing
  // space so the cursor exits the link inline node and the next keystroke
  // does not extend it.
  editor.tf.move({ unit: "offset" });
  editor.tf.insertText(" ");
}

/**
 * Insert a variable token at the editor's current caret. Used by both the
 * slash autocomplete (after deleting the `/query` trigger) and the insert
 * button. Uses snake_case (`variable.variable`) because that is the form the
 * backend `replaceVariablesInContent` substitutes at send time.
 */
export function insertVariableToken(
  editorState: UseMessageEditorResult,
  variable: MacroVariable,
): void {
  const editor = editorState.editor;
  if (!editor) {
    return;
  }
  try {
    editor.tf.focus();
    if (variable.isLink) {
      insertLinkVariableToken(editor, variable);
    } else {
      editor.tf.addMarks({
        [KEYS.color]: MACRO_TEXT_COLOR,
        [KEYS.backgroundColor]: MACRO_TEXT_BACKGROUND,
      });
      editor.tf.insertText(`{${variable.variable}}`);
      editor.tf.removeMarks([KEYS.color, KEYS.backgroundColor]);
      editor.tf.insertText(" ");
    }
  } catch {
    // noop — if the editor refuses the op we just skip the insertion.
  }
}

export function useVariableAutocomplete({
  editorState,
  enabled,
  containerRef,
}: {
  editorState: UseMessageEditorResult;
  enabled: boolean;
  containerRef: React.RefObject<HTMLElement | null>;
}): VariableAutocompleteState {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  const filteredVariables = useMemo(() => filterMacroVariables(query), [query]);

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery("");
    setFocusedIndex(0);
    setAnchorRect(null);
  }, []);

  const getCaretRect = useCallback((): DOMRect | null => {
    const selection = globalThis.getSelection?.();
    if (!selection || selection.rangeCount === 0) {
      return null;
    }
    const range = selection.getRangeAt(0).cloneRange();
    range.collapse(true);
    const caret = range.getBoundingClientRect();
    if (caret.top === 0 && caret.left === 0 && caret.width === 0) {
      const fallback = containerRef.current?.getBoundingClientRect();
      return fallback ?? null;
    }
    // Anchor the menu's horizontal position to the editor's left edge rather
    // than the exact caret x. The menu still drops below the caret line, but
    // its left aligns with the message field so users get a stable, predictable
    // position no matter where in the editor they typed `/`.
    const container = containerRef.current?.getBoundingClientRect();
    const left = container?.left ?? caret.left;
    return new DOMRect(left, caret.top, 0, caret.height);
  }, [containerRef]);

  const handleEditorChange = useHandler(() => {
    if (!enabled) {
      if (isOpen) {
        close();
      }
      return;
    }
    const editor = editorState.editor;
    if (!editor) {
      return;
    }
    const selection = editor.selection;
    if (!selection) {
      if (isOpen) {
        close();
      }
      return;
    }
    let textBeforeCaret = "";
    try {
      textBeforeCaret = editor.api.string({
        anchor: editor.api.start([]),
        focus: selection.focus,
      });
    } catch {
      textBeforeCaret = editorState.getPlainText();
    }
    const match = TRIGGER_REGEX.exec(textBeforeCaret);
    if (!match || /\s$/.test(match[0])) {
      if (isOpen) {
        close();
      }
      return;
    }
    const nextQuery = match[1] ?? "";
    setQuery(nextQuery);
    setFocusedIndex(0);
    setIsOpen(true);
    // Defer the caret rect read by one frame. Plate's onChange fires while
    // Slate's state has the new character but before React/the browser has
    // committed it to the DOM, so a synchronous `getSelection()` returns the
    // rect for the position *before* the `/` was typed. RAF lets the DOM
    // settle, then the rect matches the visual caret.
    requestAnimationFrame(() => {
      setAnchorRect(getCaretRect());
    });
  });

  const handleSelectVariable = useCallback(
    (variable: MacroVariable) => {
      const editor = editorState.editor;
      if (!editor) {
        return;
      }
      const selection = editor.selection;
      if (!selection) {
        return;
      }

      // Figure out the `/query` token that needs replacing.
      let textBeforeCaret = "";
      try {
        textBeforeCaret = editor.api.string({
          anchor: editor.api.start([]),
          focus: selection.focus,
        });
      } catch {
        textBeforeCaret = editorState.getPlainText();
      }
      const match = TRIGGER_REGEX.exec(textBeforeCaret);
      const tokenLength = match ? 1 + (match[1]?.length ?? 0) : 0;

      try {
        for (let i = 0; i < tokenLength; i++) {
          editor.tf.deleteBackward("character");
        }
        if (variable.isLink) {
          insertLinkVariableToken(editor, variable);
        } else {
          editor.tf.addMarks({
            [KEYS.color]: MACRO_TEXT_COLOR,
            [KEYS.backgroundColor]: MACRO_TEXT_BACKGROUND,
          });
          editor.tf.insertText(`{${variable.variable}}`);
          editor.tf.removeMarks([KEYS.color, KEYS.backgroundColor]);
          editor.tf.insertText(" ");
        }
      } catch {
        /* noop */
      }

      close();
      try {
        editor.tf.focus();
      } catch {
        /* noop */
      }
    },
    [editorState, close],
  );

  return {
    isOpen: isOpen && enabled && filteredVariables.length > 0,
    query,
    filteredVariables,
    focusedIndex: Math.min(
      focusedIndex,
      Math.max(filteredVariables.length - 1, 0),
    ),
    anchorRect,
    setFocusedIndex,
    close,
    handleSelectVariable,
    handleEditorChange,
  };
}
